import {
  Accordion as MuiAccordion,
  AccordionActions as MuiAccordionActions,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
} from '@mui/material';
import { styled, type Theme } from '@mui/material/styles';

import type {
  AccordionActionsProps,
  AccordionDetailsProps,
  AccordionProps,
  AccordionSummaryProps,
} from './Accordion.types';

/**
 * Styled Accordion component with container query support
 * Sets up container context and applies theme border
 */
export const StyledAccordion = styled(MuiAccordion)<AccordionProps>(({ theme }) => {
  // Type assertion to access custom theme properties
  const customTheme = theme as Theme & {
    palette: {
      borderColors?: {
        primary?: string;
        darkprimary?: string;
      };
    };
  };

  return {
    // Container query setup
    containerType: 'inline-size',
    containerName: 'accordion',

    // 2px border using theme color
    border: `2px solid ${customTheme.palette.borderColors?.primary || theme.palette.divider}`,
    borderRadius: theme.spacing(1),

    // Remove MUI default styles
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },

    // Override MUI's first/last child border radius modifications
    '&:first-of-type': {
      borderRadius: theme.spacing(1),
    },
    '&:last-of-type': {
      borderRadius: theme.spacing(1),
    },

    // Ensure consistent spacing between accordions
    '&.Mui-expanded': {
      margin: 0,
    },

    // Smooth expansion animation
    transition: theme.transitions.create(['margin', 'border-color'], {
      duration: theme.transitions.duration.short,
    }),

    // Optional: hover state
    '&:hover': {
      borderColor: customTheme.palette.borderColors?.darkprimary || theme.palette.primary.main,
    },
  };
}) as typeof MuiAccordion;

/**
 * Styled AccordionSummary with responsive padding
 * Padding increases based on container width
 */
export const StyledAccordionSummary = styled(MuiAccordionSummary)<AccordionSummaryProps>(
  ({ theme }) => ({
    // Default padding (16px)
    padding: theme.spacing(2),
    gap: theme.spacing(3),

    // Container query breakpoints for responsive padding
    '@container accordion (min-width: 501px)': {
      padding: theme.spacing(3), // Medium: 24px
    },
    // '@container accordion (min-width: 901px)': {
    //   padding: theme.spacing(4), // Large: 32px
    // },

    // Override MUI's min-height on expanded state
    '&.Mui-expanded': {
      minHeight: 'unset',
    },

    // Ensure proper alignment with expand icon
    '& .MuiAccordionSummary-content': {
      margin: 0,
      alignItems: 'center',

      // Prevent margin change on expanded
      '&.Mui-expanded': {
        margin: 0,
      },
    },

    // Style the expand icon
    '& .MuiAccordionSummary-expandIconWrapper': {
      color: theme.palette.action.active,
    },
  }),
) as typeof MuiAccordionSummary;

/**
 * Styled AccordionDetails with responsive padding
 * Content area that adjusts padding based on container width
 */
export const StyledAccordionDetails = styled(MuiAccordionDetails)<AccordionDetailsProps>(
  ({ theme }) => ({
    // Default padding (16px)
    padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,

    // Container query breakpoints for responsive padding
    '@container accordion (min-width: 501px)': {
      padding: `0 ${theme.spacing(3)} ${theme.spacing(3)}`, // Medium: 24px
    },
    // '@container accordion (min-width: 600px)': {
    //   padding: `0 ${theme.spacing(4)} ${theme.spacing(4)}`, // Large: 32px
    // },
  }),
) as typeof MuiAccordionDetails;

/**
 * Styled AccordionActions with responsive padding
 * Footer area for action buttons with responsive spacing
 */
export const StyledAccordionActions = styled(MuiAccordionActions)<AccordionActionsProps>(
  ({ theme }) => ({
    // Add top border for visual separation (full width)
    borderTop: `1px solid ${theme.palette.divider}`,

    // Use margin for X (horizontal) so border spans full width
    // Use padding for Y (vertical) for spacing from border
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: 0,
    paddingRight: 0,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),

    // Container query breakpoints for responsive spacing
    '@container accordion (min-width: 501px)': {
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      marginLeft: theme.spacing(3),
      marginRight: theme.spacing(3),
    },
    // '@container accordion (min-width: 600px)': {
    //   paddingTop: theme.spacing(4),
    //   paddingBottom: theme.spacing(4),
    //   marginLeft: theme.spacing(4),
    //   marginRight: theme.spacing(4),
    // },

    // Ensure proper button spacing
    gap: theme.spacing(1),
  }),
) as typeof MuiAccordionActions;
